import { FormInput, FormLabel, Form, Button, SocialLoginButton, Text } from '$components'
import { GoogleIcon, FacebookIcon, LogoIcon } from '$icons'
import z from 'zod'
import { useMutation } from '@tanstack/react-query'
import { tenantOnboardingRegisterMutation } from '$api-client/@tanstack/react-query.gen'
import { useAuth } from '$features/auth/hooks/use-auth'
import { QueryErrorHelper } from '$utils/query'
import { usePageTitle } from '$components'
import { Link, useSearchParams } from 'react-router-dom'
import { AuthRoutePath } from '$features/auth/routes/public'

export enum Input {
  FirstName = 'first_name',
  LastName = 'last_name',
  Email = 'email',
  Password = 'password',
  RepeatPassword = 'repeat_password',
}

const schema = z.object({
  first_name: z.string().min(1, 'First name is required').trim(),
  last_name: z.string().min(1, 'Last name is required').trim(),
  email: z.string().email('Invalid email address').trim(),
  password: z.string().min(8, 'Password must be at least 8 characters long').trim(),
  repeat_password: z.string().min(1, 'Password confirmation is required').trim(),
}).superRefine(({ password, repeat_password: repeatPassword }, ctx) => {
  if (password !== repeatPassword) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Passwords do not match',
      path: [Input.RepeatPassword],
    })
  }
})

export type RegisterFormValues = z.infer<typeof schema>

export function RegisterPage() {
  usePageTitle('Sign Up')
  const { refetchUser, signInOAuth } = useAuth()
  const [searchParams] = useSearchParams()

  const { mutate: register, isPending: isRegistering, error } = useMutation({
    ...tenantOnboardingRegisterMutation(),
    onSuccess: () => refetchUser(),
  })

  const onSubmit = async (values: RegisterFormValues) => {
    register({
      body: {
        email: values.email,
        first_name: values.first_name,
        last_name: values.last_name,
        password: values.password,
        password_confirmation: values.password,
        plan: searchParams.get('plan'),
      },
    })
  }

  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-center px-stack-section">
      <div className="flex flex-col w-full max-w-prose-sm">
        <div className="mb-stack-hero pt-stack-section self-start">
          <LogoIcon className="h-6 w-auto" />
        </div>

        <div className="flex flex-col w-full pb-stack-section">
          <Text type="h1">
            Create Account
          </Text>

          <Text
            type="p"
            className="mb-stack-grid mt-stack-tight"
          >
            Fill out the required information to create your account.
          </Text>

          <div className="flex flex-col gap-stack-tight mb-stack-section">
            <SocialLoginButton
              icon={<GoogleIcon />}
              onClick={() => signInOAuth('google')}
              disabled={isRegistering}
            >
              Continue with Google
            </SocialLoginButton>

            <SocialLoginButton
              icon={<FacebookIcon />}
              onClick={() => signInOAuth('facebook')}
              disabled={isRegistering}
            >
              Continue with Facebook
            </SocialLoginButton>
          </div>

          {/* Divider */}
          <div className="relative mb-stack-section">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border-strong" />
            </div>
            <div className="relative flex justify-center text-caption uppercase">
              <Text
                type="xs"
                className="bg-surface px-stack-tight"
              >
                Or continue with email
              </Text>
            </div>
          </div>

          <Form
            onSubmit={onSubmit}
            schema={schema}
            errors={QueryErrorHelper.getQueryValidationErrors(error)}
          >
            {() => (
              <div className="flex flex-col gap-stack-roomy">
                <div className="flex flex-col gap-stack-tight">
                  <FormLabel htmlFor={Input.FirstName}>First Name</FormLabel>
                  <FormInput name={Input.FirstName} />
                </div>

                <div className="flex flex-col gap-stack-tight">
                  <FormLabel htmlFor={Input.LastName}>Last Name</FormLabel>
                  <FormInput name={Input.LastName} />
                </div>

                <div className="flex flex-col gap-stack-tight">
                  <FormLabel htmlFor={Input.Email}>Email</FormLabel>
                  <FormInput name={Input.Email} />
                </div>

                <div className="flex flex-col gap-stack-tight">
                  <FormLabel htmlFor={Input.Password}>Create Password</FormLabel>
                  <FormInput
                    name={Input.Password}
                    type="password"
                  />
                </div>

                <div className="flex flex-col gap-stack-tight">
                  <FormLabel htmlFor={Input.RepeatPassword}>Confirm Password</FormLabel>
                  <FormInput
                    name={Input.RepeatPassword}
                    type="password"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="mt-stack-roomy"
                  isLoading={isRegistering}
                  disabled={isRegistering}
                >
                  Create Account
                </Button>

                <Text
                  type="s"
                  className="text-center"
                >
                  Already have an account?&nbsp;

                  <Link
                    to={AuthRoutePath.Login}
                    className="underline"
                  >
                    Sign in here
                  </Link>
                </Text>
              </div>
            )}
          </Form>
        </div>
      </div>
    </div>
  )
}
